import axios from 'axios';
import { toast } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin;


export { API_BASE_URL };

export const api = axios.create({
  baseURL: API_BASE_URL + '/api',
  withCredentials: true,
  transformRequest: (data, headers) => {
    if (data instanceof FormData) {
      // Ne pas transformer FormData
      return data;
    }
    return JSON.stringify(data);
  },
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 60000,
});

// Intercepteur de requête
api.interceptors.request.use((config) => {
  const requestId = uuidv4();
  config.headers['X-Request-Id'] = requestId;

  const token = localStorage.getItem('token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
}, (error) => {
  console.error('[API Request Error]', error);
  return Promise.reject(error);
});

// Intercepteur de réponse
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token?: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token?: string) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 429) {
      toast.error('Too many requests. Please try again later.');
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await api.post('/auth/refresh');
        const { token } = refreshResponse.data;
        
        localStorage.setItem('token', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        
        processQueue(null, token);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        if (globalNavigate) {
          globalNavigate('/login');
        }
        localStorage.removeItem('token');
        toast.error('Session expired. Please log in again.');
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status >= 500) {
      toast.error('Server error. Please try again later.');
    } else if (error.response?.status >= 400) {
      toast.error(error.response?.data?.message || 'Invalid request');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('Request timeout');
    } else if (!error.response) {
      toast.error('Network error');
    }

    return Promise.reject(error);
  }
);

let globalNavigate: ((path: string) => void) | null = null;

export const setAuthInterceptor = (navigate: (path: string) => void) => {
  globalNavigate = navigate;
};

export default api;